import { runAz } from "./az.js";
import { listAgentModels } from "./model-catalog.js";
import { listModelQuota } from "./quota.js";
import { ensureDeployment } from "./foundry-create.js";
import {
  WHITELIST, planCascade, walkCascade, decideNote, renderChosenModelNote,
  type TraceRow, type Rung, type RungOutcome,
} from "./model-cascade.js";

export interface IntakeModelChoice {
  /** undefined → the caller omits the override and the persona frontmatter floor applies. */
  model: string | undefined;
  note: string;
  trace: TraceRow[];
}

/** Default liveness backstop: roughly 3x the realistic worst case (five
 *  validation-fast rejections plus one successful create). */
const DEFAULT_DEADLINE_MS = 180_000;

const SCOPE_RE = /\/resourceGroups\/([^/]+)\/providers\/Microsoft\.CognitiveServices\/accounts\/([^/]+)/i;

/** The ARM id is authoritative about WHERE the Foundry account lives — more so
 *  than bootstrap state, which records what was requested rather than what exists. */
export function parseAccountScope(scope: string): { resourceGroup: string; account: string } | null {
  const m = SCOPE_RE.exec(scope);
  return m ? { resourceGroup: m[1], account: m[2] } : null;
}

/** Curated founder-facing narration. Raw ARM text stays in the trace. */
function narrate(model: string, outcome: RungOutcome, region: string): string {
  if (outcome === "deployed") return `${model} - ready`;
  return `${model} - not available for your subscription in ${region}`;
}

async function accountRegion(account: string, resourceGroup: string, fallback: string): Promise<string> {
  try {
    const out = await runAz([
      "cognitiveservices", "account", "show",
      "--name", account, "--resource-group", resourceGroup,
      "--query", "location", "-o", "tsv",
    ]);
    return out.trim() || fallback;
  } catch {
    return fallback;
  }
}

/**
 * Choose the best model this subscription can actually deploy for the intake agent.
 *
 * NEVER throws and never blocks the intake deploy: every failure path yields
 * `model: undefined`, which makes the caller omit the override so the persona
 * frontmatter floor applies — exactly today's behavior.
 *
 * Call this ONCE, before any retry loop around the agent deploy: re-walking the
 * ladder on a role-propagation retry would re-attempt every deployment.
 */
export async function resolveIntakeModel(args: {
  accountScope: string;
  fallbackRegion: string;
  onNarrate?: (msg: string) => void;
  deadlineMs?: number;
}): Promise<IntakeModelChoice> {
  const floorTrace = (outcome: RungOutcome, detail?: string): TraceRow[] =>
    WHITELIST.map((r) => ({ model: r.model, outcome, ...(detail === undefined ? {} : { detail }) }));

  /** Degrade to the persona's configured model. `detail` never reaches the founder —
   *  it exists so a contract violation leaves a trace someone can actually debug. */
  const degradeToFloor = (detail?: string): IntakeModelChoice => {
    const trace = floorTrace("catalog-unverified", detail);
    return { model: undefined, note: renderChosenModelNote(decideNote(WHITELIST, { chosen: null, trace })), trace };
  };

  const parsed = parseAccountScope(args.accountScope);
  if (!parsed) return degradeToFloor(`unparseable account scope: ${args.accountScope}`);

  try {
    const region = await accountRegion(parsed.account, parsed.resourceGroup, args.fallbackRegion);
    args.onNarrate?.("choosing the best model your subscription can run...");

    const [catalog, quota] = await Promise.all([listAgentModels(region), listModelQuota(region)]);
    const plan = planCascade(WHITELIST, catalog, quota);

    const deploy = async (rung: Rung): Promise<void> => {
      await ensureDeployment({
        account: parsed.account,
        resourceGroup: parsed.resourceGroup,
        model: rung.model,
        modelVersion: rung.version,
        capacity: rung.capacity,
        format: rung.format,
      });
    };

    const start = Date.now();
    const result = await walkCascade(WHITELIST, plan, deploy, {
      now: () => Date.now(),
      deadlineAt: start + (args.deadlineMs ?? DEFAULT_DEADLINE_MS),
      onRung: (model, outcome) => args.onNarrate?.(narrate(model, outcome, region)),
    });

    return {
      model: result.chosen?.model,
      note: renderChosenModelNote(decideNote(WHITELIST, result)),
      trace: result.trace,
    };
  } catch (e) {
    // Defensive backstop. Every dependency called above is documented to degrade
    // to a typed result rather than throw (listAgentModels, listModelQuota,
    // walkCascade's own per-rung try/catch), so this should be unreachable in
    // practice. But resolveIntakeModel's entire reason for existing is to be a
    // non-blocking improvement on a critical path: if a dependency's contract is
    // ever violated (a future change, an unforeseen edge case), an unexpected
    // exception here must still degrade to the floor rather than reject and
    // block the intake deploy.
    //
    // Capture the cause. Reaching here means a documented contract was broken, and
    // a silent degrade would leave nothing to debug from — the founder still sees
    // only the curated note, since `detail` is trace-only.
    return degradeToFloor(e instanceof Error ? e.message : String(e));
  }
}
